import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  Room,
  Message,
  UnreadCountData
} from '@/components/chat/components/types'

interface ChatState {
  rooms: Room[]
  selectedRoomId: number | null
  messages: Message[]
  loading: boolean
  typingUsers: { [key: string]: boolean }
  onlineUsers: number[]
  unreadCounts: { [roomId: number]: number }
  /** Timestamps of when each room's unread count was locally cleared.
   *  Used to ignore stale server values that arrive after the user opens a room. */
  unreadClearTimes: { [roomId: number]: number }
  threadMessages: Message[]
  activeThreadId: number | null
  threadLoading: boolean
  pinnedMessages: Message[]
  /** Tracks unread thread replies: { parentMessageId → { count, lastMessage, roomId } } */
  unreadThreads: { [parentId: number]: { count: number; lastText: string; roomId: number } }
}

const initialState: ChatState = {
  rooms: [],
  selectedRoomId: null,
  messages: [],
  loading: false,
  typingUsers: {},
  onlineUsers: [],
  unreadCounts: {},
  unreadClearTimes: {},
  threadMessages: [],
  activeThreadId: null,
  threadLoading: false,
  pinnedMessages: [],
  unreadThreads: {},
}

export const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setRooms: (state, action: PayloadAction<Room[]>) => {
      state.rooms = action.payload
    },
    selectRoom: (state, action: PayloadAction<number | null>) => {
      state.selectedRoomId = action.payload
    },
    setMessages: (state, action: PayloadAction<Message[]>) => {
      state.messages = action.payload
    },
    addMessage: (state, action: PayloadAction<Message>) => {
      const message = { ...action.payload }
      // Normalize parentMessage from populated object to numeric ID
      if (message.parentMessage && typeof message.parentMessage === 'object') {
        message.parentMessage = (message.parentMessage as { id: number }).id
      }
      state.messages.push(message)

      // Get room data from the message
      const messageRoom = action.payload.room
      const roomId =
        typeof messageRoom === 'object' ? messageRoom.id : messageRoom

      // Update the room's lastActivity and ensure name is preserved
      const roomIndex = state.rooms.findIndex((r) => r.id === roomId)
      if (roomIndex !== -1) {
        // Keep existing room data but update lastActivity
        state.rooms[roomIndex].lastActivity = new Date().toISOString()

        // If the message contains updated room info and the room is not a direct message, update it
        if (
          typeof messageRoom === 'object' &&
          messageRoom.name &&
          !state.rooms[roomIndex].isDirectMessage
        ) {
          state.rooms[roomIndex].name = messageRoom.name
        }
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    setTypingUser: (
      state,
      action: PayloadAction<{ userId: number; roomId: number; typing: boolean }>
    ) => {
      const { userId, roomId, typing } = action.payload

      // Format the key that will be used in the typingUsers object
      const key = `${roomId}-${userId}`
      if (typing) {
        // Add or update typing status
        state.typingUsers[key] = true
      } else {
        // Remove typing status
        delete state.typingUsers[key]
      }
    },
    setOnlineUsers: (state, action: PayloadAction<number[]>) => {
      state.onlineUsers = action.payload
    },
    markMessagesAsRead: (
      state,
      action: PayloadAction<{ messageIds: number[]; userId: number }>
    ) => {
      const { messageIds, userId } = action.payload

      state.messages = state.messages.map((message) => {
        if (messageIds.includes(message.id)) {
          // Make sure we don't add duplicate read receipts
          const readBy = [...(message.readBy || [])]
          if (!readBy.includes(userId)) {
            readBy.push(userId)
          }

          return {
            ...message,
            readBy
          }
        }
        return message
      })
    },
    updateRoom: (state, action: PayloadAction<Room>) => {
      const roomIndex = state.rooms.findIndex((r) => r.id === action.payload.id)
      if (roomIndex !== -1) {
        state.rooms[roomIndex] = action.payload
      }
    },
    addRoom: (state, action: PayloadAction<Room>) => {
      state.rooms.push(action.payload)
    },
    incrementUnreadCount: (state, action: PayloadAction<number>) => {
      const roomId = action.payload
      state.unreadCounts[roomId] = (state.unreadCounts[roomId] || 0) + 1
    },
    clearUnreadCount: (state, action: PayloadAction<number>) => {
      const roomId = action.payload
      state.unreadCounts[roomId] = 0
      state.unreadClearTimes[roomId] = Date.now()
      // Also clear the stale unreadMessages on the room object
      const roomIndex = state.rooms.findIndex((r) => r.id === roomId)
      if (roomIndex !== -1) {
        state.rooms[roomIndex].unreadMessages = 0
      }
    },
    /**
     * Slack-style "marked unread" flag (live ticket #41). Survives auto-mark-
     * read on room open, cleared by an explicit Mark-as-read or by sending a
     * message in the room (server clears the row + emits roomUnreadCleared).
     */
    setRoomManuallyMarkedUnread: (
      state,
      action: PayloadAction<{ roomId: number; value: boolean }>,
    ) => {
      const { roomId, value } = action.payload
      const roomIndex = state.rooms.findIndex((r) => r.id === roomId)
      if (roomIndex !== -1) {
        state.rooms[roomIndex].manuallyMarkedUnread = value
      }
      if (value) {
        if ((state.unreadCounts[roomId] || 0) < 1) state.unreadCounts[roomId] = 1
      }
    },
    setUnreadCounts: (state, action: PayloadAction<UnreadCountData[]>) => {
      // Normal rooms: server is authoritative, but protect against stale pushes
      // that arrive within 5 s of a local `clearUnreadCount` (avoids the
      // badge-briefly-flashing-back-to-N flicker on room open).
      //
      // Support_bot rooms: client is authoritative (too many server-side
      // mark-read races corrupt the server-side count). We trust the server
      // only for POSITIVE pushes; we ignore "absent from payload" and zero
      // interpretations, which would otherwise wipe the badge we just
      // incremented locally.
      const now = Date.now()
      const GRACE_PERIOD = 5000
      const isSupportBotRoom = (rid: number) =>
        state.rooms.find((r) => r.id === rid)?.room_kind === 'support_bot'
      const newCounts: Record<number, number> = {}
      for (const { roomId, unreadCount } of action.payload) {
        newCounts[roomId] = unreadCount
      }
      // Zero out rooms missing from payload.
      for (const key of Object.keys(state.unreadCounts)) {
        const rid = Number(key)
        if (rid in newCounts) continue
        if (isSupportBotRoom(rid)) continue // client-authoritative
        const clearedAt = state.unreadClearTimes[rid]
        if (clearedAt && now - clearedAt < GRACE_PERIOD) continue // respect local clear
        state.unreadCounts[rid] = 0
      }
      // Apply payload values.
      for (const { roomId, unreadCount, manuallyMarkedUnread } of action.payload) {
        if (isSupportBotRoom(roomId) && unreadCount === 0) continue // support_bot: ignore zero
        const clearedAt = state.unreadClearTimes[roomId]
        if (
          !isSupportBotRoom(roomId) &&
          clearedAt &&
          now - clearedAt < GRACE_PERIOD &&
          unreadCount > 0
        ) {
          // Normal room: a positive push within the grace of a recent clear
          // is assumed stale (pre-mark-read compute still in flight).
          continue
        }
        state.unreadCounts[roomId] = unreadCount
        if (manuallyMarkedUnread !== undefined) {
          const idx = state.rooms.findIndex((r) => r.id === roomId)
          if (idx !== -1) state.rooms[idx].manuallyMarkedUnread = manuallyMarkedUnread
        }
      }
    },
    /**
     * Verbatim application of the server's unread-counts payload — zero out
     * rooms missing from the payload, apply every value including zero, no
     * support_bot special case, no grace. Used for the first server push on a
     * fresh socket connection / handshake, where we have no local truth to
     * preserve and the server is the only source of the correct state.
     */
    setUnreadCountsTrusted: (state, action: PayloadAction<UnreadCountData[]>) => {
      const newCounts: Record<number, number> = {}
      for (const { roomId, unreadCount } of action.payload) {
        newCounts[roomId] = unreadCount
      }
      for (const key of Object.keys(state.unreadCounts)) {
        const rid = Number(key)
        if (rid in newCounts) continue
        state.unreadCounts[rid] = 0
      }
      // Reset all rooms' manuallyMarkedUnread to false; payload values below
      // re-set the flagged ones. This way fresh handshake reflects the truth.
      for (const r of state.rooms) {
        r.manuallyMarkedUnread = false
      }
      for (const { roomId, unreadCount, manuallyMarkedUnread } of action.payload) {
        state.unreadCounts[roomId] = unreadCount
        if (manuallyMarkedUnread) {
          const idx = state.rooms.findIndex((r) => r.id === roomId)
          if (idx !== -1) state.rooms[idx].manuallyMarkedUnread = true
        }
      }
    },
    updateMessage: (state, action: PayloadAction<Message>) => {
      const index = state.messages.findIndex(
        (msg) => msg.id === action.payload.id
      )
      if (index !== -1) {
        // Replace the message with the updated one
        state.messages[index] = action.payload
      }
    },
    setActiveThread: (state, action: PayloadAction<number | null>) => {
      state.activeThreadId = action.payload
      if (action.payload === null) {
        state.threadMessages = []
      }
    },
    setThreadMessages: (state, action: PayloadAction<Message[]>) => {
      state.threadMessages = action.payload
    },
    addThreadMessage: (state, action: PayloadAction<Message>) => {
      state.threadMessages.push(action.payload)
    },
    setThreadLoading: (state, action: PayloadAction<boolean>) => {
      state.threadLoading = action.payload
    },
    updateMessageReaction: (
      state,
      action: PayloadAction<{
        messageId: number
        reactions: Record<string, number[]>
      }>
    ) => {
      const { messageId, reactions } = action.payload
      // Update in main messages
      const msgIndex = state.messages.findIndex((msg) => msg.id === messageId)
      if (msgIndex !== -1) {
        state.messages[msgIndex].reactions = reactions
      }
      // Update in thread messages
      const threadIndex = state.threadMessages.findIndex(
        (msg) => msg.id === messageId
      )
      if (threadIndex !== -1) {
        state.threadMessages[threadIndex].reactions = reactions
      }
    },
    updateMessageReplyCount: (
      state,
      action: PayloadAction<{ messageId: number; replyCount: number; threadParticipants?: { id: number; username: string; img?: { url: string } | null }[] }>
    ) => {
      const { messageId, replyCount, threadParticipants } = action.payload
      const msgIndex = state.messages.findIndex((msg) => msg.id === messageId)
      if (msgIndex !== -1) {
        state.messages[msgIndex].replyCount = replyCount
        if (threadParticipants) {
          state.messages[msgIndex].threadParticipants = threadParticipants
        }
      }
    },
    patchMessage: (
      state,
      action: PayloadAction<{ messageId: number; content?: string; content_en?: string }>,
    ) => {
      const { messageId, content, content_en } = action.payload
      const apply = (m: Message) => {
        if (content !== undefined) m.content = content
        if (content_en !== undefined) m.content_en = content_en
      }
      const msgIdx = state.messages.findIndex((m) => m.id === messageId)
      if (msgIdx !== -1) apply(state.messages[msgIdx])
      const threadIdx = state.threadMessages.findIndex((m) => m.id === messageId)
      if (threadIdx !== -1) apply(state.threadMessages[threadIdx])
    },
    setPinnedMessages: (state, action: PayloadAction<Message[]>) => {
      state.pinnedMessages = action.payload
    },
    toggleMessagePin: (
      state,
      action: PayloadAction<{
        messageId: number
        isPinned: boolean
        pinnedAt: string | null
        pinnedBy: { id: number; username: string } | null
        message: Message
      }>
    ) => {
      const { messageId, isPinned, pinnedAt, pinnedBy, message } = action.payload

      // Update in main messages array
      const msgIndex = state.messages.findIndex((msg) => msg.id === messageId)
      if (msgIndex !== -1) {
        state.messages[msgIndex].isPinned = isPinned
        state.messages[msgIndex].pinnedAt = pinnedAt ?? undefined
        state.messages[msgIndex].pinnedBy = pinnedBy
      }

      // Update pinnedMessages array
      if (isPinned) {
        // Add to pinned if not already there
        if (!state.pinnedMessages.some((m) => m.id === messageId)) {
          state.pinnedMessages.unshift(message)
        }
      } else {
        // Remove from pinned
        state.pinnedMessages = state.pinnedMessages.filter((m) => m.id !== messageId)
      }
    },
    removeRoom: (state, action: PayloadAction<number>) => {
      state.rooms = state.rooms.filter((r) => r.id !== action.payload)
      if (state.selectedRoomId === action.payload) {
        state.selectedRoomId = null
      }
    },
    addUnreadThread: (
      state,
      action: PayloadAction<{ parentId: number; text: string; roomId: number }>
    ) => {
      const { parentId, text, roomId } = action.payload
      const existing = state.unreadThreads[parentId]
      state.unreadThreads[parentId] = {
        count: (existing?.count || 0) + 1,
        lastText: text,
        roomId,
      }
    },
    clearUnreadThread: (state, action: PayloadAction<number>) => {
      delete state.unreadThreads[action.payload]
    },
    setUnreadThreadsForRoom: (
      state,
      action: PayloadAction<{ roomId: number; threads: { parentId: number; count: number; lastText?: string }[] }>,
    ) => {
      // Server-sourced: replaces the set of unread-threads for THIS room,
      // keeping other rooms' entries untouched. Run on room-open so banner
      // survives reloads.
      const { roomId, threads } = action.payload
      for (const [pid, v] of Object.entries(state.unreadThreads)) {
        if (v.roomId === roomId) delete state.unreadThreads[Number(pid)]
      }
      for (const t of threads) {
        state.unreadThreads[t.parentId] = {
          count: t.count,
          lastText: t.lastText || '',
          roomId,
        }
      }
    },
    resetChatUnread: (state) => {
      state.unreadCounts = {}
      state.unreadThreads = {}
      state.unreadClearTimes = {}
    },
  }
})

export const {
  setRooms,
  selectRoom,
  setMessages,
  addMessage,
  setLoading,
  setTypingUser,
  setOnlineUsers,
  markMessagesAsRead,
  updateRoom,
  addRoom,
  incrementUnreadCount,
  clearUnreadCount,
  setRoomManuallyMarkedUnread,
  setUnreadCounts,
  setUnreadCountsTrusted,
  updateMessage,
  setActiveThread,
  setThreadMessages,
  addThreadMessage,
  setThreadLoading,
  updateMessageReaction,
  updateMessageReplyCount,
  patchMessage,
  setPinnedMessages,
  toggleMessagePin,
  removeRoom,
  addUnreadThread,
  clearUnreadThread,
  setUnreadThreadsForRoom,
  resetChatUnread,
} = chatSlice.actions

export default chatSlice.reducer
